<template>
  <v-dialog
    v-model="dialogVisible"
    fullscreen
    persistent
    transition="dialog-bottom-transition"
  >
    <v-card tile>
      <v-toolbar
        dark
        color="primary"
      >
        <v-btn
          icon
          dark
          @click="close"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
        <v-toolbar-title>チェックイン</v-toolbar-title>
      </v-toolbar>

      <v-card-text class="d-flex flex-column align-center pt-8">
        <!-- 待機画面: カメラ起動前 -->
        <template v-if="mode === 'idle'">
          <v-icon
            size="120"
            color="primary"
            class="mb-6"
          >
            {{ icons.mdiQrcodeScan }}
          </v-icon>
          <p class="text-h5 mb-6">
            QRコードを読み取ります
          </p>
          <v-btn
            x-large
            color="primary"
            @click="startScanning"
          >
            <v-icon left>
              {{ icons.mdiCamera }}
            </v-icon>
            カメラを起動
          </v-btn>
        </template>

        <!-- スキャナー -->
        <div
          v-show="mode === 'scan'"
          class="scanner-container"
        >
          <div id="checkin-qr-reader" />
          <p class="text-center mt-2 text-body-2 grey--text">
            QRコードをカメラに向けてください
          </p>
          <v-btn
            class="mt-4"
            outlined
            @click="stopScanning"
          >
            <v-icon left>
              {{ icons.mdiCameraOff }}
            </v-icon>
            カメラを終了
          </v-btn>
        </div>

        <!-- チェックイン結果 -->
        <checkin-result
          v-if="mode === 'result'"
          :result="checkinResult"
          @back="resumeScan"
        />

        <!-- エラー -->
        <v-alert
          v-if="errorMessage"
          type="error"
          outlined
          class="mt-3"
          dismissible
          @input="errorMessage = null"
        >
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  ref, watch, nextTick, onBeforeUnmount,
} from '@vue/composition-api'
import { Html5Qrcode } from 'html5-qrcode'
import {
  mdiQrcodeScan, mdiClose, mdiCamera, mdiCameraOff,
} from '@mdi/js'
import CheckinApi from '@/api/waiter/Checkin'
import CheckinResult from './CheckinResult.vue'

export default {
  name: 'CheckinDialog',
  components: { CheckinResult },
  props: {
    value: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const dialogVisible = ref(props.value)
    const mode = ref('idle') // 'idle' | 'scan' | 'result'
    const checkinResult = ref(null)
    const errorMessage = ref(null)
    let scanner = null

    const stopScanner = async () => {
      if (scanner) {
        try {
          const state = scanner.getState()
          if (state === 2) { // SCANNING
            await scanner.stop()
          }
        } catch {
          // ignore
        }
        scanner = null
      }
    }

    const onScanSuccess = async decodedText => {
      await stopScanner()
      mode.value = 'result'
      errorMessage.value = null

      try {
        const res = await CheckinApi.checkin(decodedText)
        checkinResult.value = res.data
      } catch (err) {
        checkinResult.value = null
        errorMessage.value = 'チェックインに失敗しました'
        mode.value = 'scan'
        // eslint-disable-next-line no-use-before-define
        await startScanner()
      }
    }

    const startScanner = async () => {
      mode.value = 'scan'
      await nextTick()
      const el = document.getElementById('checkin-qr-reader')
      if (!el) return

      try {
        scanner = new Html5Qrcode('checkin-qr-reader')
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          onScanSuccess,
        )
      } catch (err) {
        errorMessage.value = 'カメラの起動に失敗しました'
        mode.value = 'idle'
      }
    }

    const startScanning = () => {
      errorMessage.value = null
      startScanner()
    }

    const stopScanning = async () => {
      await stopScanner()
      mode.value = 'idle'
    }

    const resumeScan = async () => {
      checkinResult.value = null
      await startScanner()
    }

    const close = async () => {
      await stopScanner()
      mode.value = 'idle'
      checkinResult.value = null
      errorMessage.value = null
      dialogVisible.value = false
      emit('input', false)
    }

    watch(() => props.value, async newVal => {
      dialogVisible.value = newVal
      if (!newVal) {
        await stopScanner()
        mode.value = 'idle'
      }
    })

    onBeforeUnmount(() => {
      stopScanner()
    })

    return {
      dialogVisible,
      mode,
      checkinResult,
      errorMessage,
      startScanning,
      resumeScan,
      close,
      stopScanning,
      icons: {
        mdiQrcodeScan, mdiClose, mdiCamera, mdiCameraOff,
      },
    }
  },
}
</script>

<style scoped>
.scanner-container {
  min-height: 300px;
  width: 100%;
  max-width: 500px;
}
</style>
